import { getLogger } from '@wk/elm-uui-common';
import {
    CHMessagingScope,
    EventType,
    IDialog,
    IItemScreen,
    IOverlayDialog,
    IQueueItem,
    IQueueServiceInterface,
    IRefreshUUIForEntity,
    IToast,
    QueueItemStatus,
    ScreenName,
    unsubscribe,
} from '@wk/elm-uui-context-handler';
import { format } from 'date-fns';
import { uniqueId, groupBy } from 'lodash';
import { useEffect } from 'react';
import {
    manuallyDecrementPromiseCounter,
    manuallyIncrementPromiseCounter,
    manuallyResetPromiseCounter,
} from 'react-promise-tracker';
import { IAlertDialog, IAlertDialogButton } from '../components/common/alertDialog';
import { clGetQueue, clSubscribe } from '../components/contextLayerService/contextLayerService';
import { messageBusDispatch } from '../components/contextLayerService/messageBusService';
import { UUIMessageBusAction } from '../components/contextLayerService/types';
import { IItemScreenProps } from '../components/itemScreen/itemScreen';
import { IContextLayerConfig, NotificationType, UUIReduxDispatch } from '../reducers/types';
import { useReduxDispatch } from './useReduxDispatch';
import { useReduxSelector } from './useReduxSelector';

export const useContextLayerSubscriptions = (): void => {
    const reduxDispatch = useReduxDispatch();
    const contextLayerConfig = useReduxSelector((state) => state.contextLayerConfig);
    const logoutUrl = useReduxSelector((state) => state.appResources.logoutUrl);

    useEffect(() => {
        const toastSubId = subscribeToToastMessages(reduxDispatch);
        const dialogSubId = subscribeToDialogs(reduxDispatch);
        const itemScreenSubId = subscribeToItemScreens(reduxDispatch, contextLayerConfig);
        const refreshUUIForEntitySubId = subscribeToRefreshUUIForEntity(reduxDispatch);
        const overlaySubId = subscribeToOverlays(reduxDispatch);
        const overlayCloseSubId = subscribeToOverlayClose(reduxDispatch);
        const globalSpinnerSubId = subscribeToGlobalSpinner();

        let queuedId: string;
        let startedId: string;
        let completedId: string;
        let failedId: string;
        let processingId: string;

        clGetQueue().then((queueManager: IQueueServiceInterface) => {
            if (!queueManager) {
                return;
            }
            failedId = queueManager.elementsFailed
                .subscribe((queueItems: IQueueItem[]) => {
                    reduxDispatch({
                        type: 'SetUploadHistoryItems',
                        status: QueueItemStatus.FAILED,
                        uploadHistoryItems: queueItems,
                    });
                })
                .toString();

            queuedId = queueManager.elementsQueued
                .subscribe((queueItems: IQueueItem[]) => {
                    reduxDispatch({
                        type: 'SetUploadHistoryItems',
                        status: QueueItemStatus.NEW,
                        uploadHistoryItems: queueItems,
                    });
                })
                .toString();

            processingId = queueManager.elementsProcessing
                .subscribe((queueItems: IQueueItem[]) => {
                    reduxDispatch({
                        type: 'SetUploadHistoryItems',
                        status: QueueItemStatus.PROCESSING,
                        uploadHistoryItems: queueItems,
                    });
                })
                .toString();

            startedId = queueManager.elementsStarted
                .subscribe((queueItems: IQueueItem[]) => {
                    const data = groupBy(queueItems, 'status');

                    if (data[QueueItemStatus.STARTED]) {
                        reduxDispatch({
                            type: 'SetUploadHistoryItems',
                            status: QueueItemStatus.STARTED,
                            uploadHistoryItems: data[QueueItemStatus.STARTED],
                        });
                    }

                    if (data[QueueItemStatus.PENDING]) {
                        reduxDispatch({
                            type: 'SetUploadHistoryItems',
                            status: QueueItemStatus.PENDING,
                            uploadHistoryItems: data[QueueItemStatus.PENDING],
                        });
                    }
                })
                .toString();

            completedId = queueManager.elementsCompleted
                .subscribe((queueItems: IQueueItem[]) => {
                    reduxDispatch({
                        type: 'SetUploadHistoryItems',
                        status: QueueItemStatus.COMPLETED,
                        uploadHistoryItems: queueItems,
                    });
                })
                .toString();
        });

        return () => {
            unsubscribe(toastSubId);
            unsubscribe(dialogSubId);
            unsubscribe(itemScreenSubId);
            unsubscribe(queuedId);
            unsubscribe(startedId);
            unsubscribe(completedId);
            unsubscribe(failedId);
            unsubscribe(processingId);
            unsubscribe(refreshUUIForEntitySubId);
            unsubscribe(overlaySubId);
            unsubscribe(overlayCloseSubId);
            unsubscribe(globalSpinnerSubId);
        };
    }, [contextLayerConfig, logoutUrl, reduxDispatch]);
};

const subscribeToRefreshUUIForEntity = (reduxDispatch: UUIReduxDispatch): string => {
    return clSubscribe(EventType.REFRESH_UUI_FOR_ENTITY, (refreshUUIForEntity: IRefreshUUIForEntity) => {
        getLogger('RefreshUUIForEntity').info(
            'Refresh UUI For Entity Requested: ' + format(new Date(), 'hh:mm:ss.SS a'),
        );
        reduxDispatch({
            type: 'RefreshUUIForEntity',
            refreshUUIForEntity,
        });
    });
};

const subscribeToOverlayClose = (reduxDispatch: UUIReduxDispatch): string => {
    return clSubscribe(EventType.OVERLAY_CLOSE, () => {
        reduxDispatch({ type: 'CloseOverlayDialog' });
    });
};

const subscribeToOverlays = (reduxDispatch: UUIReduxDispatch): string => {
    return clSubscribe(EventType.OVERLAY, (overlay: IOverlayDialog) => {
        manuallyResetPromiseCounter();
        reduxDispatch({
            type: 'OpenOverlayDialog',
            overlayDialog: overlay,
        });
    });
};

const subscribeToGlobalSpinner = (): string => {
    return clSubscribe(EventType.GLOBAL_SPINNER, (isOpen: boolean) => {
        if (isOpen) {
            manuallyIncrementPromiseCounter();
        } else {
            manuallyDecrementPromiseCounter();
        }
    });
};

const subscribeToToastMessages = (reduxDispatch: UUIReduxDispatch): string => {
    return clSubscribe(EventType.TOAST, (toast: IToast, restrictToCurrInstance = false) => {
        const uniqueKey = uniqueId(Date.now().toString());
        const busAction = getShowToastBusAction(toast, uniqueKey);
        let message = '';
        if (typeof toast.toastMessage === 'string') {
            message = toast.toastMessage;
        } else {
            message = JSON.stringify(toast.toastMessage);
        }

        if (!restrictToCurrInstance) {
            messageBusDispatch(busAction);
        }

        reduxDispatch({
            type: 'ShowNotification',
            notification: {
                key: uniqueKey,
                message: message,
                options: {
                    variant: (['default', 'information'].includes(toast.type)
                        ? 'info'
                        : toast.type) as NotificationType,
                    persist: toast.type.includes('success') ? false : true,
                },
            },
        });
    });
};

export const getShowToastBusAction = (toast: IToast, uniqueKey: string): UUIMessageBusAction => {
    const toastType = toast.type.toString();
    let message = '';
    if (typeof toast.toastMessage === 'string') {
        message = toast.toastMessage;
    } else {
        message = JSON.stringify(toast.toastMessage);
    }
    const toastObjString = JSON.stringify({ toastType: toastType, key: uniqueKey, message: message });
    const messageBusObj: UUIMessageBusAction = {
        type: 'ShowToast',
        scope: CHMessagingScope.OtherInstances,
        message: toastObjString,
        options: {
            variant: (['default', 'information'].includes(toastType) ? 'info' : toastType) as NotificationType,
            persist: toastType.includes('success') ? false : true,
        },
    };
    return messageBusObj;
};

const subscribeToDialogs = (reduxDispatch: UUIReduxDispatch): string => {
    return clSubscribe(EventType.DIALOG, (dialog: IDialog) => {
        const alertDialogButtons: IAlertDialogButton[] = dialog.buttons.map((button) => ({
            label: button.label,
            onClick: button.callback,
        }));
        const alertDialog: IAlertDialog = {
            id: uniqueId('dialog_'),
            title: dialog.title,
            message: dialog.message,
            buttons: alertDialogButtons,
            onClose: dialog.onCloseCallback,
        };
        // hide global spinner if there is one.
        manuallyResetPromiseCounter();
        reduxDispatch({
            type: 'OpenAlertDialog',
            alertDialog,
        });
    });
};

const subscribeToItemScreens = (reduxDispatch: UUIReduxDispatch, contextLayerConfig: IContextLayerConfig): string => {
    return clSubscribe(EventType.ITEMSCREEN, (itemScreen: IItemScreen) => {
        const itemScreenProps = convertSubscribeArgToItemScreenProps(itemScreen, contextLayerConfig);
        // hide global spinner if there is one.
        manuallyResetPromiseCounter();
        reduxDispatch({
            type: 'OpenItemScreenDialog',
            itemScreenProps,
        });
    });
};

export const convertSubscribeArgToItemScreenProps = (
    itemScreen: IItemScreen,
    contextLayerConfig: IContextLayerConfig,
): IItemScreenProps => {
    let screenId = -1;
    switch (itemScreen.screenName) {
        case ScreenName.QUICKFILE_ADD_DOCUMENT:
            screenId = contextLayerConfig.quickFileAddDocumentScreenId;
            break;
        case ScreenName.CHECKIN_DOCUMENT:
            screenId = contextLayerConfig.checkInScreenId;
            break;
    }
    if (!screenId || screenId === -1) {
        throw `No cooresponding screen id found for context layer item screen request: ${itemScreen.screenName}`;
    }

    return {
        mode: 'add',
        screenId: screenId,
        popupTitle: itemScreen.title,
        initialValues: itemScreen.payload,
        contextLayerInfo: itemScreen.context
            ? {
                  associatedEntityId: itemScreen.context.associatedEntityId,
                  associatedEntityType: itemScreen.context.associatedEntityType,
                  associatedEntityTypeId: itemScreen.context.associatedEntityTypeId!,
                  associatedEntityName: itemScreen.context.associatedEntityName!,
                  folderId: itemScreen.context.folderId,
                  folderArr: itemScreen.context.folderArr,
                  documentId: itemScreen.context.documentId,
                  isQuickFile: itemScreen.context.isQuickFile,
                  isDragAndDrop: itemScreen.context.isDragAndDrop,
                  filePaths: itemScreen.context.filePaths,
              }
            : undefined,
    };
};

export default useContextLayerSubscriptions;
